import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Card } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Layout from "../../../../layout/default";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaUserAlt,
    FaUserPlus,
    FaPhoneAlt,
    FaIdCard,
    FaRegIdCard,
    FaUserTag,
} from "react-icons/fa";
import { FaSave } from "react-icons/fa";
import { FaTimesCircle } from "react-icons/fa";
import { FaUser, FaPhone, FaEnvelope } from "react-icons/fa";

const UserDetailEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        phone_1: "",
        email: "",
    });

    useEffect(() => {
        const fetchUserDetails = async (id) => {
            try {
                Swal.fire({
                    title: 'Loading...',
                    text: 'Fetching your details. Please wait.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });

                const authToken = localStorage.getItem("authToken");
                if (!authToken) {
                    navigate("/shop/auths/login", { replace: true });
                    return;
                }

                const response = await axios.get(`${process.env.REACT_APP_HOST}api/user-details/${id}`, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });

                const userDetails = response.data.data;

                setFormData({
                    first_name: userDetails.first_name,
                    last_name: userDetails.last_name,
                    phone_1: userDetails.phone_1,
                    email: userDetails.email || "", // Ensure empty string if null
                });

            } catch (error) {
                console.error("Error fetching user details:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load user details.',
                });
            }
            finally {
                Swal.close();
            }
        };

        fetchUserDetails(id);
    }, [id, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
            Swal.fire({
                icon: "error",
                title: "Authentication Required",
                text: "Please log in to proceed.",
            });
            navigate("/shop");
            return;
        }

        // Phone Number Validation (must be 10 digits)
        if (!/^\d{10}$/.test(formData.phone_1)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Input',
                text: 'Phone number must be exactly 10 digits.',
            });
            return;
        }

        try {
            const authToken = localStorage.getItem("authToken");

            // Create a payload with non-empty fields only
            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone_1: formData.phone_1,
                ...(formData.email && { email: formData.email }), // Only include email if it's not empty
            };

            const response = await axios.put(
                `${process.env.REACT_APP_HOST}api/user-details/${id}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                });

            // Show success Swal with the style you requested
            Swal.fire({
                imageUrl: "/shop/images/Edit.svg",
                imageWidth: '100%',
                imageHeight: 'auto',
                imageAlt: "Welcome",
                title: "Profile Updated Successfully!",
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                customClass: {
                    image: 'full-size-image'
                }
            }).then(() => {
                navigate("/shop/auths/user-profile-page");
            });

        } catch (error) {
            debugger;
            let errorMessage = "Failed to update details. Please try again.";
            if (error.response && error.response.data) {
                errorMessage = error.response.data.message || error.response.data.errors || errorMessage;
            }
            Swal.fire({
                imageUrl: "/shop/images/Failed_1.svg",
                imageWidth: '100%',
                imageHeight: 'auto',
                title: 'Error',
                text: errorMessage,
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                customClass: {
                    image: 'full-size-image'
                }
            });
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const clear = () => {
        navigate("/shop/auths/user-profile-page");
    };

    return (
        <Layout title="Edit User Details">
            <Container className="mt-4">
                <Row className="justify-content-md-center mt-4">
                    <Col md={8} className="shadow p-4 rounded">
                        <h3 className="mb-4 text-center text-primary">
                            Update User Details
                        </h3>

                        <Form onSubmit={handleSubmit}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>First Name</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FaUser />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Last Name</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FaUser />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Phone Number 1</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FaPhone />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                name="phone_1"
                                                value={formData.phone_1}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email (Optional)</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FaEnvelope />
                                            </span>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                            // Remove required attribute to make it optional
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mt-4">
                                <Col className="d-flex justify-content-start">
                                    <Button
                                        variant="success"
                                        type="submit"
                                        className="px-4 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                                        style={{ minHeight: '42px', fontSize: '1rem' }}
                                    >
                                        Update
                                    </Button>
                                </Col>

                                <Col className="d-flex justify-content-end">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={clear}
                                        className="px-4 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                                        style={{ minHeight: '42px', fontSize: '1rem' }}
                                    >
                                        Cancel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>
            </Container>
            <br />
            <br />
        </Layout>
    );
};

export default UserDetailEdit;